import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchFoodIngredients, updateFoodIngredient, type FoodIngredient } from "../foodApi";
import { COST_SOURCE_LABEL, money, shortDate, unitMoney } from "../utils/foodFormatters";
import { groupByCategory } from "../utils/groupByCategory";
import { QueryError } from "../components/QueryError";
import { GroupedTable } from "../components/GroupedTable";
import {
  ArchiveButton,
  ArchivedBadge,
  ListToolbar,
  useExpandedGroups,
} from "../components/ListToolbar";
import { AddIngredientModal } from "../components/AddIngredientModal";

function costPerUnitCell(r: FoodIngredient): ReactNode {
  if (r.costPerUnit == null) return "-";
  return (
    <span title={r.costPerUnitSource ? COST_SOURCE_LABEL[r.costPerUnitSource] : undefined}>
      {unitMoney(r.costPerUnit)}
      <span className="text-tribal-muted"> / {r.standardUnit}</span>
      {r.costPerUnitSource === "override" && (
        <span className="ml-1 text-xs text-tribal-muted">(manual)</span>
      )}
    </span>
  );
}

function latestCostCell(r: FoodIngredient): ReactNode {
  if (r.latestLineTotal == null) return "-";
  return (
    <span>
      {money(r.latestLineTotal)}
      {r.latestLineQuantity != null && (
        <span className="text-xs text-tribal-muted">
          {" "}
          for {r.latestLineQuantity} {r.latestLineUnit}
        </span>
      )}
    </span>
  );
}

export function IngredientsView() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const q = useQuery({ queryKey: ["food", "ingredients"], queryFn: fetchFoodIngredients });
  const searching = search.trim() !== "";
  const groupsState = useExpandedGroups(searching);

  const archiveMutation = useMutation({
    mutationFn: (r: FoodIngredient) => updateFoodIngredient(r.id, { isActive: !r.isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["food"] });
    },
  });

  const all = q.data ?? [];
  const activeCount = all.filter((r) => r.isActive).length;
  const archivedCount = all.length - activeCount;
  const groups = useMemo(
    () => groupByCategory(showArchived ? all : all.filter((r) => r.isActive), search),
    [all, showArchived, search],
  );

  const columns: { key: string; title: string; render: (r: FoodIngredient) => ReactNode }[] = [
    {
      key: "name",
      title: "Name",
      render: (r) => (
        <>
          <Link to={`/food/ingredients/${r.id}`} className="text-tribal-red hover:underline">
            {r.name}
          </Link>
          {!r.isActive && <ArchivedBadge />}
        </>
      ),
    },
    { key: "unit", title: "Unit", render: (r) => r.standardUnit },
    { key: "costPerUnit", title: "Cost/unit", render: costPerUnitCell },
    { key: "latestCost", title: "Latest Cost", render: latestCostCell },
    { key: "latestCostDate", title: "Cost Date", render: (r) => shortDate(r.latestCostDate) },
    { key: "lineItemCount", title: "Invoice Uses", render: (r) => r.lineItemCount },
    { key: "menuItemCount", title: "Menu Uses", render: (r) => r.menuItemCount },
    {
      key: "actions",
      title: "",
      render: (r) => (
        <ArchiveButton
          isActive={r.isActive}
          pending={archiveMutation.isPending && archiveMutation.variables?.id === r.id}
          onClick={() => archiveMutation.mutate(r)}
        />
      ),
    },
  ];

  if (q.isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-20 text-sm text-tribal-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading ingredients...
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <QueryError message={`Failed to load ingredients: ${(q.error as Error).message}`} />;
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-medium text-tribal-muted">
          {activeCount} ingredient{activeCount !== 1 ? "s" : ""}
          {archivedCount > 0 && ` · ${archivedCount} archived`}
        </h2>
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-tribal-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark"
        >
          <Plus className="h-4 w-4" />
          Add Ingredient
        </button>
      </div>
      <ListToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search ingredients"
        showArchived={showArchived}
        onShowArchived={setShowArchived}
        onExpandAll={() => groupsState.expandAll(groups.map((g) => g.category))}
        onCollapseAll={groupsState.collapseAll}
      />
      {archiveMutation.isError && (
        <p className="mb-3 rounded-lg bg-red-500/15 px-3 py-2 text-sm text-red-300">
          {(archiveMutation.error as Error).message}
        </p>
      )}
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <GroupedTable<FoodIngredient>
          groups={groups}
          columns={columns}
          isExpanded={groupsState.isExpanded}
          onToggle={groupsState.toggle}
          rowClassName={(r) => (r.isActive ? "" : "opacity-60")}
          empty={searching ? `No ingredients match "${search.trim()}".` : "No ingredients found."}
        />
      </section>
      {showAdd && <AddIngredientModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}
